import { Box, Grid, Paper, Stack, Typography, useMediaQuery } from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useMemo } from 'react';
import groupBy from 'lodash.groupby';
import { roundNum } from '../../../../../helpers';
import { formatCount } from '../../../../../utils/D3/helper';
import { parseTabData } from '../../../../../utils/helpers';
import Chart from '../../../../_common/VisxChart';
import { default as AreaChart } from '../../../../_common/VisxChart/AreaChart';
import { colors } from '../../../../../utils/designTokens';

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? 'rgb(10, 25, 41)' : '#fff',
  ...theme.typography.body2,
  height: useMediaQuery((theme) => theme.breakpoints.down('sm')) ? '' : '100%',
  marginBottom: '5px',
  color: theme.palette.text.secondary,
  boxShadow: theme.palette.mode === 'dark' ? 0 : '0 0.5px 1px rgb(255 255 255 / 20%), 0 2px 3px rgb(255 255 255 / 10%)',
  border: theme.palette.mode === 'dark' ? '1px solid rgb(19, 47, 76)' : '1px solid #eae9ee',
  borderRadius: '7px',
  '& .Item-Head': {
    background: '#f2f2f2',
    borderBottom: '1px solid #eae9ee',
    padding: '0 8px 0 8px',
    borderRadius: '6px 6px 0 0',
    '& a': { textDecoration: 'none' },
    '& .option-title': {
      fontWeight: 500,
      color: '#333',
      fontSize: '.875rem',
      paddingBottom: 0,
    }
  },
  '& .Item-Body': {
    padding: theme.spacing(0),
    paddingTop: 0,
    color: '#13113c'
  }
}));

const MetricRow = ({ metricConfig, metricValue }) => {
  if (!metricConfig || !metricValue || metricValue.value == null) return null;

  const value = metricValue.value;
  const isPositive = Math.sign(value) > 0;

  const headingText = isPositive ? metricConfig.heading : (metricConfig.negHeading || metricConfig.heading);
  const rawItemProp = headingText?.replace(/[^a-zA-Z0-9]/g, '').replace(/\s/g, '');

  let formattedValue = '';
  let valueColor = 'inherit';

  if (metricConfig.isPct) {
    if (metricConfig.formula) {
      if (metricConfig.formula === "#val*100") {
        formattedValue = `${roundNum(value * 100, 2)}%`;
      } else if (metricConfig.formula === "abs(#val)") {
        formattedValue = `${roundNum(Math.abs(value), 2)}%`;
      } else {
        try {
          const evaluated = new Function('val', `return ${metricConfig.formula.replace(/#val/g, 'val')}`)(value);
          formattedValue = `${roundNum(evaluated, 2)}%`;
        } catch (err) {
          console.error('Formula evaluation error', err);
          formattedValue = `${formatCount(Math.abs(value), true, 1, Math.sign(value))}%`;
        }
      }
    } else {
      valueColor = Math.sign(value) < 0 ? '#DB3353' : 'inherit';
      formattedValue = `${formatCount(Math.abs(value), true, 1, Math.sign(value))}%`;
    }
  } else {
    formattedValue = formatCount(Math.abs(value), true, 1, Math.sign(value));
  }

  return (
    <Box className="s-card shadow-none pt-3 pb-0" sx={{ mt: .2 }}>
      <Stack spacing={1.2} direction="row" alignItems="center" justifyContent="space-between" className="s-assets-title" itemProp={rawItemProp}>
        <Box component='div' className="s-assets-title">
          <Typography itemProp="name" variant="body2" className="td-cap" sx={{ fontSize: '.875rem !important', fontWeight: '600 !important', color: colors.primary[800] }}>
            {headingText}
          </Typography>
        </Box>
        <Box component='div' className="s-assets-title">
          <Typography itemProp="price" variant="subtitle2" className="td-cap" sx={{ fontSize: '.875rem !important', fontWeight: '600 !important', color: valueColor }}>
            {formattedValue}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
};

const ChartGridItem = ({
  seriesCode,
  ticker,
  info,
  metricDataConfig,
  chartItemBase,
  metricDataItem,
  divideColumnsBy,
  metSeriesData
}) => {

  const tickerSeriesCode = seriesCode.replace(/XXXXX/g, ticker);
  const chartHeading = metricDataConfig?.chartHeading || metricDataConfig?.heading || tickerSeriesCode;

  const transformedData = useMemo(() => {
    return metricDataItem?.map(item => ({
      date: new Date(item.recordDate),
      value: item.value,
    }));
  }, [metricDataItem]);

  const chartData = useMemo(() => {
    if (!transformedData || !chartItemBase) return null;
    const chartDetails = metricDataItem?.[metricDataItem.length - 1];
    const chartRecordDate = chartDetails?.recordDate || '';

    return {
      ...chartItemBase,
      series: (chartItemBase.series || []).map(d => ({ ...d, yformula: tickerSeriesCode })),
      xSeries: chartItemBase.x_series,
      ySeries: tickerSeriesCode,
      fromDate: chartRecordDate,
      toDate: "",
      series_data: transformedData,
      stock_data: [],
    };
  }, [transformedData, chartItemBase, tickerSeriesCode, metricDataItem]);

  if (!chartData || !metricDataItem || metricDataItem.length === 0) return null;

  return (
    <Grid size={{ xs: 12, sm: 12, md: divideColumnsBy || 12 }}>
      <Box component="div" className="option-content">
        <Box className="crisischart">
          <Box sx={{
            borderBottom: '1px solid #eae9ee', borderTop: '1px solid #eae9ee',
            padding: '0 8px', verticalAlign: 'middle', background: '#fbfbfc',
          }}>
            <Typography component="h4" className="option-title" itemProp="name"
              sx={{ fontSize: '.875rem !important', color: '#70708F !important', fontWeight: '500 !important' }}>
              {ticker} {chartHeading}
            </Typography>
          </Box>

          {metricDataConfig?.metrics && metricDataConfig.metrics.length > 0 && (
            <Box component="div" className="Item-Body" sx={{ pb: '0 !important', mx: '8px !important' }}>
              <Box className="mb-0 bar-card s-assets" component="div" sx={{ pb: .5, mb: 0, mt: .8 }}>
                {metricDataConfig.metrics.slice(0, 5).map((met, idx) => {
                  const metSeriesKey = met?.seriesCode?.replace(/XXXXX/g, ticker);
                  const metricValues = metSeriesData ? metSeriesData[metSeriesKey] : null;
                  return (
                    <MetricRow
                      key={`${metSeriesKey}-${idx}`}
                      metricConfig={met}
                      metricValue={metricValues?.[0]}
                    />
                  );
                })}
              </Box>
            </Box>
          )}

          <Box component="div" className="option-content Item-Body" sx={{ width: '100%', height: "100%", mt: 0, pb: '0 !important' }}>
            {seriesCode.includes('REV.TTM') || seriesCode.includes('PS') ? (
              <AreaChart data={chartData} _height={150} _margin={{ top: 10, right: 40, bottom: 25, left: 20 }} />
            ) : (
              <Chart data={chartData} _height={150} _margin={{ top: 10, right: 45, bottom: 25, left: 20 }} />
            )}
          </Box>
        </Box>
      </Box>
    </Grid>
  );
};

const MetricsCharts = ({
  tabData, info, themes, chart_state, secHeading, divideColumnsBy,
  noOfChartsNeeded, columnWidth, height, onlyMetrics, desk
}) => {
  const ticker = info?.ticker || '';

  const parsedISSeries = useMemo(() => {
    const rawData = tabData?.find(d => d.section_code === "ISSeries");
    return rawData ? parseTabData(rawData) : null;
  }, [tabData]);

  const parsedLatestMetrics = useMemo(() => {
    const rawData = tabData?.find(d => d.section_code === "latest_metrics");
    return rawData ? parseTabData(rawData) : null;
  }, [tabData]);

  const parsedMetricChartItem = useMemo(() => {
    const rawData = tabData?.find(d => d.section_code === "Metrics");
    return rawData ? parseTabData(rawData) : null;
  }, [tabData]);

  const metricsCharts = useMemo(() => {
    return parsedISSeries?.settings?.order?.find(d => d.secHeading === secHeading) || null;
  }, [parsedISSeries, secHeading]);

  const metSeriesData = useMemo(() => {
    return parsedLatestMetrics?.tabData ? groupBy(parsedLatestMetrics.tabData, d => d.code) : null;
  }, [parsedLatestMetrics]);

  if (!metricsCharts || (!metricsCharts.metrics && !metricsCharts.charts?.length)) {
    return null;
  }

  const allChartSeriesItems = useMemo(() => {
    const items = [];

    if (metricsCharts.charts?.length > 0) {
      metricsCharts.charts.forEach(chartConfig => {
        chartConfig?.seriesCode?.forEach((sCode, index) => {
          items.push({ sCode, config: chartConfig, source: 'charts' });
        });
      });
    } else if (metricsCharts.seriesCode?.length > 0) {
      metricsCharts.seriesCode.forEach((sCode, index) => {
        const matchingMetric = metricsCharts.metrics?.find(m => m.seriesCode === sCode);
        items.push({ sCode, config: matchingMetric || metricsCharts, source: 'wrapper' });
      });
    }

    return items;
  }, [metricsCharts]);

  return (
    <Grid size={{ xs: 12, sm: 12, md: columnWidth || 12 }}>
      <Item className="crisischart">
        <Box sx={{ display: 'flex', alignItems: 'flex-start' }} className="Item-Head">
          <Typography itemProp="name" component="h3" className="option-title" sx={{ mr: 'auto' }}>
            {ticker} {metricsCharts.secHeading}
          </Typography>
        </Box>

        <Box sx={{ width: '100%', mt: 0, mb: .7 }}>
          {metricsCharts.metrics && (
            <Box component="div" className="Item-Body" sx={{ pb: '0 !important', mx: '8px !important' }}>
              <Box className="mb-0 bar-card s-assets" component="div" sx={{ pt: .5, pb: 1, mb: 0 }}>
                {metricsCharts.metrics.slice(0, 5).map((d, i) => {
                  const metSeriesKey = d?.seriesCode?.replace(/XXXXX/g, ticker);
                  const metricValues = metSeriesData ? metSeriesData[metSeriesKey] : null;
                  return (
                    <MetricRow
                      key={`${metSeriesKey}-${i}`}
                      metricConfig={d}
                      metricValue={metricValues?.[0]}
                    />
                  );
                })}
              </Box>
            </Box>
          )}

          {allChartSeriesItems.length > 0 && (
            <Grid container spacing={1.2}>
              {allChartSeriesItems.slice(0, noOfChartsNeeded).map((item, idx) => {
                const metricDataItem = parsedISSeries?.tabData?.filter(
                  d => d.code === item.sCode.replace(/XXXXX/g, ticker)
                );
                const chartItemBase = parsedMetricChartItem?.tabData || null;

                return (
                  <ChartGridItem
                    key={`${item.sCode}-${idx}`}
                    seriesCode={item.sCode}
                    ticker={ticker}
                    info={info}
                    metricDataConfig={item.config}
                    chartItemBase={chartItemBase}
                    metricDataItem={metricDataItem}
                    divideColumnsBy={divideColumnsBy}
                    metSeriesData={metSeriesData}
                  />
                );
              })}
            </Grid>
          )}
        </Box>
      </Item>
    </Grid>
  );
};

export default React.memo(MetricsCharts);